import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Unlink, RefreshCw, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { instances } from '../api';
import BridgeAmpel from './BridgeAmpel';
import { partnerName, rollenText } from './VerknuepfungsLeiste';

/**
 * Zwei Instanzen desselben Kunden verbinden — heute: oj.fm bettet open.task als
 * „Projekte" ein.
 *
 * Der gemeinsame Schlüssel entsteht im PC und geht über die `.env` an beide
 * Seiten. Deshalb steht überall dabei, dass eine Änderung erst nach dem Ausrollen
 * **beider** Instanzen wirkt: der Wert liegt in zwei Dateien auf womöglich zwei
 * verschiedenen Hosts, und wer nur eine Seite ausrollt, hat eine halb umgestellte
 * Verbindung — die Fehlermeldung erscheint dann in der jeweils anderen App.
 */
export default function InstanceLinksPanel({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [gewaehlt, setGewaehlt] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [fehler, setFehler] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['instances', instanceId, 'links'],
    queryFn: () => instances.getLinks(instanceId),
  });

  const auffrischen = () => qc.invalidateQueries({ queryKey: ['instances', instanceId, 'links'] });
  const alsFehler = (err: unknown) =>
    setFehler(
      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Aktion fehlgeschlagen.',
    );

  const verknuepfen = useMutation({
    mutationFn: () => instances.createLink(instanceId, gewaehlt),
    onSuccess: () => {
      setGewaehlt('');
      setFehler('');
      setHinweis('Verknüpft. Beide Instanzen müssen neu ausgerollt werden, damit der Schlüssel ankommt.');
      auffrischen();
    },
    onError: alsFehler,
  });

  const loesen = useMutation({
    mutationFn: (linkId: string) => instances.removeLink(instanceId, linkId),
    onSuccess: () => {
      setFehler('');
      setHinweis('Verknüpfung gelöst. Der Schlüssel bleibt bei der eingebetteten Instanz stehen.');
      auffrischen();
    },
    onError: alsFehler,
  });

  const erneuern = useMutation({
    mutationFn: (linkId: string) => instances.rotateLinkKey(instanceId, linkId),
    onSuccess: (r) => {
      setFehler('');
      setHinweis(
        `Neuer Schlüssel (${r.keyPrefix}…). Bis beide Instanzen neu ausgerollt sind, ist die Verbindung unterbrochen.`,
      );
      auffrischen();
    },
    onError: alsFehler,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2].map((i) => <div key={i} className="h-12 rounded-lg bg-slate-100" />)}
      </div>
    );
  }

  const links = data?.links ?? [];
  const kandidaten = data?.candidates ?? [];
  const name = partnerName;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-surface">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-700">{t('instances.linkedInstances')}</h3>
          <p className="mt-0.5 text-xs text-slate-400">
            oj.fm bettet open.task als „Projekte" und die eLibrary ein; ein Medusa-Shop holt
            Leseprobe und Medien bei oj.fm. Die Liste zeigt beide
            Richtungen, auf beiden Seiten. Der gemeinsame Schlüssel kommt aus dem PC und geht
            über die <code className="font-mono">.env</code> an beide Seiten.
          </p>
        </div>

        <div className="divide-y divide-slate-100">
          {!links.length && (
            <p className="px-5 py-4 text-sm italic text-slate-400">{t('instances.noLink')}</p>
          )}
          {links.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <Link2 size={15} className="shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="shrink-0 text-xs text-slate-400">{rollenText(l)}</span>
                  <Link
                    to={`/instances/${l.partner.id}`}
                    className="truncate font-mono text-sm text-slate-800 hover:text-brand-700 hover:underline"
                  >
                    {partnerName(l.partner)}
                  </Link>
                </div>
                <div className="text-xs text-slate-400">
                  {l.partner.productName} · {l.kind}
                  {l.partner.status !== 'ACTIVE' && (
                    <span className="text-amber-600"> · Partner {l.partner.status}</span>
                  )}
                  {l.ohneSchluessel
                    ? <> · ohne Schlüssel (Shop liest Leseprobe und Medien öffentlich)</>
                    : l.hasKey
                      ? <> · Schlüssel <code className="font-mono">{l.keyPrefix}…</code></>
                      : <span className="text-amber-600"> · noch kein Schlüssel</span>}
                </div>
                {l.kind === 'medusa-shop' && (
                  <div className="mt-0.5 text-xs text-slate-400">
                    Katalog und Preise: in ojfm unter Verwaltung → Shop-Integrationen → Medusa
                    eintragen (API-Schlüssel aus dem Medusa-Admin).
                  </div>
                )}
              </div>
              {!l.ohneSchluessel && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Schlüssel erneuern? Bis beide Instanzen neu ausgerollt sind, ist die Verbindung unterbrochen.'))
                      erneuern.mutate(l.id);
                  }}
                  disabled={erneuern.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  <RefreshCw size={12} /> Schlüssel erneuern
                </button>
              )}
              <button
                type="button"
                onClick={() => { if (confirm('Verknüpfung lösen?')) loesen.mutate(l.id); }}
                disabled={loesen.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
              >
                <Unlink size={12} /> Lösen
              </button>
              {/* Über die ganze Breite, unter den Knöpfen: ein hinterlegter
                  Schlüssel sagt nichts darüber, ob die beiden Apps miteinander
                  reden. Die Prüfung stellt am Ende eine echte Anfrage. */}
              <div className="w-full pl-6">
                <BridgeAmpel instanceId={instanceId} linkId={l.id} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-5 py-4">
          <select
            value={gewaehlt}
            onChange={(e) => setGewaehlt(e.target.value)}
            className="min-w-64 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">
              {kandidaten.length
                ? '— Instanz wählen —'
                : 'Keine passende Instanz: derselbe Kunde und eine Software, zu der es eine Brücke gibt'}
            </option>
            {kandidaten.map((c) => (
              <option key={c.id} value={c.id}>
                {name(c)} · {c.application?.name ?? c.product.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!gewaehlt || verknuepfen.isPending}
            onClick={() => verknuepfen.mutate()}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {verknuepfen.isPending ? 'Verknüpfe…' : 'Verknüpfen'}
          </button>
        </div>
      </div>

      {hinweis && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {hinweis}
        </div>
      )}
      {fehler && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {fehler}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <span>
          Der Schlüssel geht als <code className="font-mono">BRIDGE_API_KEY</code> an open.task
          und als <code className="font-mono">OPENTASK_SERVICE_TOKEN</code> an oj.fm, dazu die
          Adresse der eingebetteten Instanz. Ohne Verknüpfung erzeugt open.task beim Start wie
          bisher selbst einen und legt ihn ins gemeinsame Volume — das bleibt der Weg für
          Installationen, die ohne PC laufen.
        </span>
      </div>
    </div>
  );
}
